import { EditorialPageLayout } from '@/components/seo/EditorialPageLayout';

export default function WhySayItNowExists() {
  return (
    <EditorialPageLayout
      path="/why-say-it-now-exists"
      ogImage="/og/why-say-it-now-exists.jpg"
      title="Why Say It Now Exists — Our Mission"
      metaDescription="Say It Now exists because the most loving words shouldn't wait for a funeral. Read the quiet, urgent reason this platform was built — and what we're trying to change."
      eyebrow="Our reason"
      h1="Why Say It Now exists."
      lede="Because the kindest words we ever speak are usually spoken to a room full of people who are not the person we are speaking about."
      articleSchema={{
        headline: 'Why Say It Now exists',
        description:
          'The mission behind Say It Now: to celebrate people while they are alive, and to make heartfelt expression a normal, ordinary part of how we love each other.',
      }}
      related={[
        { label: 'What is a Living Announcement?', href: '/what-is-a-living-announcement', hint: 'The format we built around this idea.' },
        { label: 'Affirmations and emotional wellness', href: '/affirmations-and-emotional-wellness', hint: 'The science behind being told you matter.' },
        { label: 'Living tribute ideas', href: '/living-tribute-ideas', hint: 'For when you want to honor someone, now.' },
      ]}
    >
      <p>
        We built Say It Now after watching too many eulogies. Beautiful,
        specific, devastatingly true — and just slightly too late. Every one
        of them said something the deceased never got to hear in those exact
        words, in that exact order, with that exact weight.
      </p>

      <p>
        That is the gap we are trying to close. Not dramatically. Just
        practically. With a tool that makes it normal — easy, even — to put
        the love into a form before it has to become grief.
      </p>

      <h2>The pattern we are pushing against</h2>
      <p>
        Modern life is good at urgency and bad at significance. We can send
        anything, instantly, anywhere. And yet the most significant thing we
        could send — <em>I love you. Here is exactly why. Here is what you
        gave me</em> — almost never gets typed.
      </p>

      <p>
        We default to assuming there is more time. There usually is. Until
        there isn't. Say It Now is built for the in-between: the long stretch
        where everyone you love is still here, and you have the rest of your
        life to start telling them.
      </p>

      <h2>What we believe</h2>
      <ol>
        <li>
          <strong>Emotion before monetization.</strong> The free tier really is
          free. You can send something that matters without ever paying us
          anything.
        </li>
        <li>
          <strong>Consent before reach.</strong> Nothing about your recipient
          gets shared without you choosing to share it. No quiet broadcasts.
        </li>
        <li>
          <strong>Words over performance.</strong> Living Announcements are
          private by default. This is not a feed. It is a letter.
        </li>
        <li>
          <strong>Now over later.</strong> The whole product is shaped around
          the moment you almost typed something and then closed the app.
        </li>
      </ol>

      <h2>What we are not</h2>
      <p>
        We are not a social network. We are not a memorial site. We are not
        a greeting-card service. We are a small, deliberate tool that exists
        for one reason: to make it likelier that the people you love hear
        what you would have said about them — from you, in your voice, while
        they are still here.
      </p>

      <blockquote>Celebrate people while they're alive.</blockquote>

      <p>
        That sentence is the entire company. Everything we build either
        serves it or we don't ship it.
      </p>

      <h2>If this resonates</h2>
      <p>
        Start with one person. <a href="/what-is-a-living-announcement">
        Learn what a Living Announcement is</a>, then write one. It will take
        less time than you think and matter more than you expect.
      </p>
    </EditorialPageLayout>
  );
}
